import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  type OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { type MatCheckboxChange } from '@angular/material/checkbox';
import { MatSort } from '@angular/material/sort';
import { type RawInlineNodes, toTextContent } from '@principle-theorem/editor';
import {
  extendSortingDataAccessor,
  ObservableDataSource,
  timestampSortingAccessor,
  TrackByFunctions,
} from '@principle-theorem/ng-shared';
import {
  type AutomationEntity,
  type IAutomationListData,
  MarketingCampaignStepConversion,
} from '@principle-theorem/principle-core';
import {
  AUTOMATION_CREATOR_ICON_MAP,
  AUTOMATION_CREATOR_LABEL_MAP,
  AUTOMATION_TYPES_LABEL_MAP,
  AutomationCreator,
  type AutomationType,
  isGeneratedTask,
} from '@principle-theorem/principle-core/interfaces';
import {
  DATE_TIME_FORMAT,
  Firestore,
  isWithRef,
  multiSwitchMap,
} from '@principle-theorem/shared';
import { from, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export type AutomationLayoutMode = 'table' | 'list';

@Component({
  selector: 'pr-automation-list-display',
  templateUrl: './automation-list-display.component.html',
  styleUrls: ['./automation-list-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AutomationListDisplayComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  trackByData =
    TrackByFunctions.nestedField<IAutomationListData>('automation.uid');
  listData$: ReplaySubject<IAutomationListData[]> = new ReplaySubject(1);
  @Output() selectAutomation = new EventEmitter<string>();
  @Output()
  unselectAutomation = new EventEmitter<string>();
  @Output()
  editAutomation = new EventEmitter<AutomationEntity>();
  @Output()
  cancelAutomation = new EventEmitter<AutomationEntity>();
  @Output()
  restoreAutomation = new EventEmitter<AutomationEntity>();
  @Output()
  resendAutomation = new EventEmitter<AutomationEntity>();
  @Output()
  previewAutomation = new EventEmitter<AutomationEntity>();
  @Input() multiSelect = false;
  @Input() layout: AutomationLayoutMode = 'list';
  readonly dateFormat = DATE_TIME_FORMAT;
  dataSource: ObservableDataSource<IAutomationListData>;
  displayedColumns: string[] = [
    'select',
    'icon',
    'title',
    'type',
    'createdBy',
    'patient',
    'appointment',
    'triggerDate',
    'status',
    'more',
  ];

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  constructor() {
    const automations$ = this.listData$.pipe(
      multiSwitchMap((automationData) => {
        const automation = automationData.automation;
        if (automation.creator.type !== AutomationCreator.MarketingCampaign) {
          return of(automationData);
        }

        if (automation.campaignEntryRef) {
          const campaignRef = Firestore.getParentDocRef(
            automation.campaignEntryRef
          );
          return of({
            ...automationData,
            campaignEntry: {
              id: automation.campaignEntryRef.id,
              campaignId: campaignRef.id,
            },
          } satisfies IAutomationListData);
        }

        if (!isWithRef(automation)) {
          return of(automationData);
        }

        return from(
          MarketingCampaignStepConversion.fromAutomation(automation)
        ).pipe(
          catchError(() => of(undefined)),
          map((campaignStepConversion) => {
            if (!campaignStepConversion) {
              return automationData;
            }

            return {
              ...automationData,
              campaignEntry: {
                id: MarketingCampaignStepConversion.entryRef(
                  campaignStepConversion
                ).id,
                campaignId: campaignStepConversion.campaignRef.id,
              },
            } satisfies IAutomationListData;
          })
        );
      })
    );
    this.dataSource = new ObservableDataSource(automations$);
    this.dataSource.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  @Input()
  set automations(automations: IAutomationListData[]) {
    if (automations) {
      this.listData$.next(automations);
    }
  }

  toggle(change: MatCheckboxChange, automation: AutomationEntity): void {
    if (change.checked) {
      return this.selectAutomation.emit(automation.uid);
    }
    this.unselectAutomation.emit(automation.uid);
  }

  getTitle(automation: AutomationEntity): RawInlineNodes {
    return isGeneratedTask(automation.data)
      ? automation.data.title
      : [toTextContent(automation.data.name)];
  }

  getTypeLabel(type: AutomationType): string {
    return AUTOMATION_TYPES_LABEL_MAP[type];
  }

  getCreatorLabel(creatorType: AutomationCreator): string {
    return AUTOMATION_CREATOR_LABEL_MAP[creatorType];
  }

  getCreatorIcon(creatorType: AutomationCreator): string {
    return AUTOMATION_CREATOR_ICON_MAP[creatorType];
  }

  private _sortingDataAccessor(
    data: IAutomationListData,
    sortHeaderId: string
  ): string | number | undefined {
    switch (sortHeaderId) {
      case 'status':
        return data.automation.status;
      case 'type':
        return data.automation.type;
      case 'triggerDate':
        return timestampSortingAccessor(data.automation.triggerDate);
      case 'createdAt':
        return isWithRef(data)
          ? timestampSortingAccessor(data.automation.createdAt)
          : undefined;
      default:
        return;
    }
  }
}
